import type { UIMessage } from '@ai-sdk/react';
import type { RefObject } from 'react';
import type { BliksyChatAttachment } from '@bliks-io/contracts';

/**
 * Request preparation for the Bliksy chat transport.
 * Extracts the text content from the last message and packages it
 * with the session ID for the backend.
 *
 * Only the latest user message is sent to the server (backend loads full
 * history from DB).
 *
 * The attachments arrive through a REF because the transport builds the request
 * synchronously inside the same call that dispatches the turn — state written
 * beside the dispatch would not be visible yet. The ref holds the turn currently
 * being sent, which is also the turn `regenerate` re-sends.
 */
export function prepareSendMessagesRequest(
  messages: UIMessage[],
  sessionIdRef: RefObject<string | undefined>,
  attachmentsRef: RefObject<BliksyChatAttachment[]>
) {
  const lastMessage = messages[messages.length - 1];
  // AI SDK v5: UIMessage uses parts array instead of content string
  const textPart = lastMessage.parts?.find((p) => p.type === 'text');
  const content = textPart && 'text' in textPart ? textPart.text : '';
  const attachments = attachmentsRef.current;

  return {
    body: {
      sessionId: sessionIdRef.current,
      message: {
        id: lastMessage.id,
        content
      },
      // Omitted rather than sent empty: the field is optional and an empty array
      // would claim the turn had attachments that were all dropped.
      ...(attachments.length > 0 ? { attachments } : {})
    }
  };
}
